/**
 * React Query Configuration for Cyrenza Admin App
 * 
 * This file provides centralized configuration for React Query:
 * - Query client with optimized defaults
 * - Query key factory for consistent cache keys
 * - Polling interval constants
 * - Type-safe query key structure
 */

import { QueryClient } from '@tanstack/react-query';

// ==================== QUERY CLIENT CONFIGURATION ====================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Cache data for 10 minutes before garbage collection
      gcTime: 10 * 60 * 1000, // 10 minutes (replaces cacheTime)
      
      // Retry failed requests up to 3 times
      retry: 3,
      
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on mount if data is stale
      refetchOnMount: true,
      
      // Don't refetch on window focus (admin app behavior)
      refetchOnWindowFocus: false,
      
      // Refetch when network reconnects
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      
      // Error handling
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

// ==================== POLLING INTERVALS ====================

export const pollingIntervals = {
  /**
   * Very fast polling (2 seconds) - For critical real-time data
   * Use for: Active task executions, live monitoring
   */
  veryFast: 2000,
  
  /**
   * Fast polling (5 seconds) - For active operations
   * Use for: Active tenant operations, user activity
   */
  fast: 5000,
  
  /**
   * Normal polling (30 seconds) - For general updates
   * Use for: Dashboard stats, user lists, tenant lists
   */
  normal: 30000,
  
  /**
   * Slow polling (60 seconds) - For less critical data
   * Use for: Analytics, usage reports
   */
  slow: 60000,
} as const;

// ==================== QUERY KEYS FACTORY ====================

/**
 * Centralized query keys factory for type-safe cache key management
 * 
 * Benefits:
 * - Type safety: TypeScript ensures correct key structure
 * - Consistency: All queries use the same key format
 * - Easy invalidation: Invalidate related queries easily
 * - Auto-completion: IDE suggests available keys
 * 
 * Usage:
 * ```typescript
 * const { data } = useQuery({
 *   queryKey: queryKeys.users.list({ page: 1, limit: 50 }),
 *   queryFn: () => adminApi.listUsers(1, 50)
 * });
 * ```
 */
export const queryKeys = {
  // ==================== AUTHENTICATION ====================
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  
  // ==================== USER MANAGEMENT ====================
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: { page?: number; limit?: number; search?: string; role?: string; is_active?: boolean; tenant_id?: string }) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  
  // ==================== TENANT MANAGEMENT ====================
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: (filters: { page?: number; limit?: number; status?: string }) =>
      [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
    validation: (id: string) => [...queryKeys.tenants.all, 'validate', id] as const,
  },
  
  // ==================== ANALYTICS ====================
  analytics: {
    all: ['analytics'] as const,
    overview: () => [...queryKeys.analytics.all, 'overview'] as const,
    usage: (period: string, tenant_id?: string) =>
      [...queryKeys.analytics.all, 'usage', { period, tenant_id }] as const,
    revenue: () => [...queryKeys.analytics.all, 'revenue'] as const,
    topUsers: (period: string) => [...queryKeys.analytics.all, 'top-users', period] as const,
    topAiEmployees: (period: string) => [...queryKeys.analytics.all, 'top-ai-employees', period] as const,
  },
  
  // ==================== ACTIVITY LOG ====================
  activity: {
    all: ['activity'] as const,
    feed: (filters: { limit?: number; action?: string }) =>
      [...queryKeys.activity.all, 'feed', filters] as const,
  },
} as const;

// ==================== TYPE EXPORTS ====================

export type QueryKeys = typeof queryKeys;
export type PollingInterval = typeof pollingIntervals[keyof typeof pollingIntervals];
